import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API, BACKEND_URL, STORE_SLUG, BACK_OPTION_LABELS } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { LogOut, Download, Package, DollarSign, Boxes, QrCode, AlertTriangle, Shirt, UploadCloud, Megaphone, CalendarDays, Plus, Trash2, Inbox, Star, Printer } from "lucide-react";

const STATUSES = ["New", "Printing", "Ready for Pickup", "Shipped", "Completed"];
const FRONT_LABELS = { crest: "War Eagles Crest", vintage: "Vintage 2026-27", varsity: "GRAY Volleyball (Bold)" };
const BACK_LABELS_ADMIN = { front_only: "Front Only", team_back: "Roster", name_number: "Player Number" };
const getItemImages = (it, prodMap) => {
  const p = prodMap[it.product_name];
  const color = p?.colors?.find((c) => c.name === it.garment_color);
  const front = it.front_image || color?.fronts?.[it.front_design] || color?.front_image || p?.front_image;
  let back = null;
  if (it.back_option === "team_back") back = it.back_image || color?.back_image;
  else if (it.back_option === "name_number") back = color?.name_number_back || it.back_image || color?.back_image;
  return { front, back };
};
const TABS = [
  { key: "orders", label: "Orders", icon: Package },
  { key: "products", label: "Products", icon: Shirt },
  { key: "fundraising", label: "Fundraising", icon: DollarSign },
  { key: "inventory", label: "Inventory", icon: Boxes },
  { key: "gameday", label: "Game Day QR", icon: QrCode },
  { key: "settings", label: "Store & Banners", icon: Megaphone },
  { key: "schedule", label: "Schedule", icon: CalendarDays },
  { key: "inquiries", label: "Team Inquiries", icon: Inbox },
  { key: "senior", label: "Senior Night", icon: Star },
];

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");

  useEffect(() => { if (!loading && !user) navigate("/admin/login"); }, [user, loading, navigate]);
  if (loading || !user) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-gray-400">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="border-b border-white/10 px-4 sm:px-6 py-4 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Dad Jerzeys • Team Stores</p>
          <h1 className="font-display text-3xl">GRAY VOLLEYBALL — ADMIN</h1>
        </div>
        <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout" className="flex items-center gap-2 text-sm text-gray-400 hover:text-white"><LogOut className="w-4 h-4" /> Logout</button>
      </div>

      <div className="px-4 sm:px-6 border-b border-white/10 flex gap-1 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-bold uppercase tracking-wider whitespace-nowrap border-b-2 transition-colors ${tab === t.key ? "border-[var(--gv-blue)] text-white" : "border-transparent text-gray-500"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="p-4 sm:p-6">
        {tab === "orders" && <Orders />}
        {tab === "products" && <ProductManager />}
        {tab === "fundraising" && <Fundraising />}
        {tab === "inventory" && <Inventory />}
        {tab === "gameday" && <GameDayQR />}
        {tab === "settings" && <StoreSettings />}
        {tab === "schedule" && <ScheduleManager />}
        {tab === "inquiries" && <TeamInquiries />}
        {tab === "senior" && <SeniorNightOrders />}
      </div>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const [prodMap, setProdMap] = useState({});
  const [selected, setSelected] = useState(null);
  const load = () => api.get("/admin/orders", { params: { store_slug: STORE_SLUG } }).then((r) => setOrders(r.data));
  useEffect(() => {
    load();
    api.get(`/stores/${STORE_SLUG}/products`).then((r) => {
      const m = {}; (r.data || []).forEach((p) => { m[p.name] = p; }); setProdMap(m);
    }).catch(() => {});
  }, []);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/orders/${id}/status`, { status });
    toast.success("Status updated");
    load();
  };
  const exportCsv = () => {
    const token = localStorage.getItem("gv_admin_token");
    window.open(`${API}/admin/orders/export?store_slug=${STORE_SLUG}&token=${token}`, "_blank");
  };
  const itemImages = (it) => getItemImages(it, prodMap);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-400">{orders.length} orders</p>
        <button onClick={exportCsv} data-testid="export-csv-btn" className="flex items-center gap-2 bg-[var(--gv-blue)] hover:bg-[var(--gv-blue-bright)] text-black px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors"><Download className="w-4 h-4" /> Export CSV</button>
      </div>
      <div className="overflow-x-auto border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-[#141518] text-gray-400 text-xs uppercase tracking-wider">
            <tr>{["Order #", "Date", "Customer", "Items", "Amount", "Payment", "Fulfillment", "Status"].map((h) => <th key={h} className="text-left px-3 py-3 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody data-testid="orders-table">
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-white/5 align-top">
                <td className="px-3 py-3 font-bold whitespace-nowrap"><button onClick={() => setSelected(o)} data-testid={`view-order-${o.order_number}`} className="text-[var(--gv-blue-bright)] hover:underline">{o.order_number}</button></td>
                <td className="px-3 py-3 text-gray-400 whitespace-nowrap">{o.created_at?.slice(0, 10)}</td>
                <td className="px-3 py-3 whitespace-nowrap">{o.customer_name}<br /><span className="text-xs text-gray-500">{o.email}</span></td>
                <td className="px-3 py-3">
                  {o.items.map((it, i) => {
                    const { front, back } = itemImages(it);
                    return (
                      <div key={i} className="flex gap-2 mb-2 last:mb-0">
                        <div className="flex gap-1 flex-shrink-0">
                          {front && <img src={front} alt="Front design" data-testid="order-item-front-img" className="w-14 h-14 object-cover border border-white/10 rounded bg-white/5" />}
                          {back && <img src={back} alt="Back design" data-testid="order-item-back-img" className="w-14 h-14 object-cover border border-white/10 rounded bg-white/5" />}
                        </div>
                        <div className="text-xs">
                          <span className="font-semibold text-white">{FRONT_LABELS[it.front_design] || it.front_design_label || it.product_name || "Item"}</span> — {it.garment_color ? `${it.garment_color} • ` : ""}{it.size} × {it.quantity}<br />
                          <span className="text-gray-500">
                            {/hoodie/i.test(it.product_name) ? "Hoodie" : "Tee"} • {BACK_LABELS_ADMIN[it.back_option] || it.back_option}
                            {it.back_option === "name_number" && it.player_name ? ` • ${it.player_name} #${it.player_number}${it.team ? ` (${it.team})` : ""}` : ""}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </td>
                <td className="px-3 py-3 font-bold text-[var(--gv-blue-bright)] whitespace-nowrap">${o.total?.toFixed(2)}</td>
                <td className="px-3 py-3 text-xs whitespace-nowrap">{o.payment_method}<br /><span className={o.payment_status === "paid" ? "text-green-400" : "text-yellow-500"}>{o.payment_status}</span></td>
                <td className="px-3 py-3 text-xs whitespace-nowrap">{o.fulfillment === "shipping" ? "USPS Shipping" : (o.pickup_location || "Pickup")}</td>
                <td className="px-3 py-3">
                  <select value={o.status} onChange={(e) => updateStatus(o.id, e.target.value)} data-testid={`status-select-${o.order_number}`}
                    className="bg-black border border-white/10 px-2 py-1 text-xs">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected && <OrderModal order={selected} prodMap={prodMap} onClose={() => setSelected(null)} onStatus={updateStatus} />}
    </div>
  );
}

function printPackingSlip(order, prodMap) {
  const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const rows = order.items.map((it) => {
    const { front, back } = getItemImages(it, prodMap);
    const label = FRONT_LABELS[it.front_design] || it.front_design_label || it.product_name;
    const garment = /hoodie/i.test(it.product_name) ? "Hoodie" : "Tee";
    const backLabel = BACK_LABELS_ADMIN[it.back_option] || it.back_option;
    const player = it.back_option === "name_number" && it.player_name
      ? `<div style="margin-top:4px;font-weight:bold">${esc(it.player_name)} #${esc(it.player_number || "")}${it.team ? " (" + esc(it.team) + ")" : ""}</div>` : "";
    const imgs = `${front ? `<img src="${front}" />` : ""}${back ? `<img src="${back}" />` : ""}`;
    return `<tr>
      <td class="imgs">${imgs}</td>
      <td><b>${esc(label)}</b><br>${garment}${it.garment_color ? " &bull; " + esc(it.garment_color) : ""} &bull; Size ${esc(it.size)}<br><span style="color:#555">${esc(backLabel)}</span>${player}</td>
      <td class="qty">&times;${it.quantity}</td>
    </tr>`;
  }).join("");
  const isShip = order.fulfillment === "shipping";
  const dest = isShip
    ? `<b>SHIP — USPS</b><br>${esc(order.shipping_address || "").replace(/\n/g, "<br>")}`
    : `<b>PICKUP</b>${order.pickup_team ? ` — TEAM ${esc(order.pickup_team)}` : ""}<br>${esc(order.pickup_location || "Team Pickup")}`;
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Packing Slip ${order.order_number}</title>
    <style>
      *{box-sizing:border-box} body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:0;padding:24px}
      .head{background:#0A0A0A;color:#F2B705;padding:14px 18px;font-weight:bold;letter-spacing:1px;font-size:18px;text-align:center}
      .meta{display:flex;justify-content:space-between;margin:18px 0;font-size:14px}
      .box{border:1px solid #ddd;padding:12px;border-radius:6px;min-width:45%}
      table{width:100%;border-collapse:collapse;margin-top:10px;font-size:13px}
      th{background:#f4f4f4;text-align:left;padding:8px;border-bottom:2px solid #ddd}
      td{padding:10px 8px;border-bottom:1px solid #eee;vertical-align:top}
      .imgs img{width:70px;height:70px;object-fit:cover;border:1px solid #ddd;border-radius:4px;margin-right:6px}
      .qty{text-align:right;font-weight:bold;white-space:nowrap}
      .foot{margin-top:16px;font-size:14px;text-align:right}
      @media print{body{padding:0}.no-print{display:none}}
    </style></head><body>
    <div class="head">GCA WAR EAGLES VOLLEYBALL &mdash; PACKING SLIP</div>
    <div class="meta">
      <div class="box"><b>Order #${esc(order.order_number)}</b><br>${(order.created_at || "").slice(0, 10)}<br>Payment: ${esc(order.payment_method || "")} (${esc(order.payment_status || "")})</div>
      <div class="box">${esc(order.customer_name || "")}<br>${esc(order.email || "")}${order.phone ? "<br>" + esc(order.phone) : ""}<br><br>${dest}</div>
    </div>
    <table><thead><tr><th>Design</th><th>Details</th><th style="text-align:right">Qty</th></tr></thead><tbody>${rows}</tbody></table>
    <div class="foot"><b>Total: $${(order.total || 0).toFixed(2)}</b></div>
    <p style="color:#999;font-size:11px;margin-top:24px">Powered by Dad Jerzeys &bull; dadjerzeys.com</p>
    </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 600);
}

function OrderModal({ order, prodMap, onClose, onStatus }) {
  const [status, setStatus] = useState(order.status);
  const isShip = order.fulfillment === "shipping";
  const changeStatus = async (s) => { setStatus(s); await onStatus(order.id, s); };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4" data-testid="order-modal" onClick={onClose}>
      <div className="bg-[#141518] border border-white/10 max-w-2xl w-full my-8 rounded" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-500">Order</p>
            <h3 className="font-display text-2xl text-white">{order.order_number}</h3>
          </div>
          <button onClick={onClose} data-testid="order-modal-close" className="text-gray-400 hover:text-white text-2xl leading-none">&times;</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Customer</p>
              <p className="text-white">{order.customer_name}</p>
              <p className="text-gray-400 text-xs">{order.email}</p>
              {order.phone && <p className="text-gray-400 text-xs">{order.phone}</p>}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">{isShip ? "Shipping" : "Pickup"}</p>
              <p className="text-white text-xs whitespace-pre-line">{isShip ? (order.shipping_address || "USPS") : (order.pickup_location || "Team Pickup")}</p>
              {!isShip && order.pickup_team && <p className="text-[var(--gv-blue-bright)] text-xs font-bold mt-0.5" data-testid="modal-pickup-team">Team: {order.pickup_team}</p>}
              <p className="text-gray-400 text-xs mt-1">{order.payment_method} • <span className={order.payment_status === "paid" ? "text-green-400" : "text-yellow-500"}>{order.payment_status}</span></p>
            </div>
          </div>

          <div className="space-y-3">
            {order.items.map((it, i) => {
              const { front, back } = getItemImages(it, prodMap);
              const label = FRONT_LABELS[it.front_design] || it.front_design_label || it.product_name;
              return (
                <div key={i} className="flex gap-3 border border-white/10 rounded p-3" data-testid={`modal-item-${i}`}>
                  <div className="flex gap-2 flex-shrink-0">
                    {front && <img src={front} alt="Front" className="w-28 h-28 object-cover border border-white/10 rounded bg-white/5" />}
                    {back && <img src={back} alt="Back" className="w-28 h-28 object-cover border border-white/10 rounded bg-white/5" />}
                  </div>
                  <div className="text-sm">
                    <p className="font-bold text-white">{label}</p>
                    <p className="text-gray-400 text-xs mt-1">{/hoodie/i.test(it.product_name) ? "Hoodie" : "Tee"}{it.garment_color ? ` • ${it.garment_color}` : ""} • Size {it.size} • Qty {it.quantity}</p>
                    <p className="text-gray-400 text-xs">{BACK_LABELS_ADMIN[it.back_option] || it.back_option}</p>
                    {it.back_option === "name_number" && it.player_name && (
                      <p className="text-[var(--gv-blue-bright)] text-xs font-semibold mt-1">{it.player_name} #{it.player_number}{it.team ? ` (${it.team})` : ""}</p>
                    )}
                    <p className="text-gray-300 text-xs mt-1">${it.line_total?.toFixed(2)}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-sm text-right space-y-1 border-t border-white/10 pt-3">
            <p className="text-gray-400">Subtotal: <span className="text-white">${order.subtotal?.toFixed(2)}</span></p>
            {order.shipping_cost > 0 && <p className="text-gray-400">Shipping: <span className="text-white">${order.shipping_cost?.toFixed(2)}</span></p>}
            <p className="font-bold text-[var(--gv-blue-bright)]">Total: ${order.total?.toFixed(2)}</p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-white/10">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 uppercase tracking-wider">Status</label>
            <select value={status} onChange={(e) => changeStatus(e.target.value)} data-testid="modal-status-select" className="bg-black border border-white/10 px-2 py-1 text-xs text-white">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <button onClick={() => printPackingSlip(order, prodMap)} data-testid="print-packing-slip" className="flex items-center gap-2 bg-[var(--gv-blue)] hover:bg-[var(--gv-blue-bright)] text-black px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors">
            <Package className="w-4 h-4" /> Print Packing Slip
          </button>
        </div>
      </div>
    </div>
  );
}

function Fundraising() {
  const [data, setData] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const load = () => api.get("/admin/fundraising", { params: { store_slug: STORE_SLUG, start, end } }).then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div><label className="block text-xs text-gray-400 mb-1">Start</label><input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="fund-start" className="bg-black border border-white/10 px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs text-gray-400 mb-1">End</label><input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="fund-end" className="bg-black border border-white/10 px-3 py-2 text-sm" /></div>
        <button onClick={load} data-testid="fund-apply" className="bg-[var(--gv-blue)] hover:bg-[var(--gv-blue-bright)] text-black px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors">Apply</button>
      </div>
      {data && (
        <div className="grid sm:grid-cols-3 gap-4" data-testid="fundraising-metrics">
          <Metric label="Total Merchandise Sales" value={`$${data.total_sales.toFixed(2)}`} sub={`${data.order_count} orders`} />
          <Metric label="15% Gray Volleyball Contribution" value={`$${data.contribution.toFixed(2)}`} accent />
          <Metric label="Net Sales After Contribution" value={`$${data.net_sales.toFixed(2)}`} />
        </div>
      )}
    </div>
  );
}

const INQUIRY_STATUSES = ["New Lead", "Contacted", "Meeting Scheduled", "Proposal Sent", "Store Build", "Active Partner", "Not Moving Forward"];

function TeamInquiries() {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  const load = () => api.get("/admin/team-inquiries").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.patch(`/admin/team-inquiries/${id}`, { status }); toast.success("Status updated"); load(); };
  const saveNotes = async (id, admin_notes) => { await api.patch(`/admin/team-inquiries/${id}`, { admin_notes }); toast.success("Notes saved"); };
  const exportCsv = () => { const token = localStorage.getItem("gv_admin_token"); window.open(`${API}/admin/team-inquiries/export?token=${token}`, "_blank"); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-400" data-testid="inquiries-count">{rows.length} inquiries</p>
        <button onClick={exportCsv} data-testid="inquiries-export" className="flex items-center gap-2 text-sm border border-white/10 px-3 py-2 hover:border-[var(--gv-blue)]"><Download className="w-4 h-4" /> Export CSV</button>
      </div>
      {rows.length === 0 && <p className="text-gray-500 text-sm">No inquiries yet.</p>}
      <div className="space-y-3">
        {rows.map((i) => (
          <div key={i.id} className="border border-white/10 bg-[#141518]" data-testid={`inquiry-${i.id}`}>
            <div className="p-4 flex flex-wrap items-center justify-between gap-3 cursor-pointer" onClick={() => setOpenId(openId === i.id ? null : i.id)}>
              <div>
                <p className="font-bold">{i.organization || i.contact_name}{i.sport ? <span className="text-gray-500 font-normal"> • {i.sport}</span> : null}</p>
                <p className="text-xs text-gray-400">{i.contact_name} • {i.email} • {i.created_at?.slice(0, 10)}</p>
              </div>
              <select value={i.status} onClick={(e) => e.stopPropagation()} onChange={(e) => setStatus(i.id, e.target.value)} data-testid={`inquiry-status-${i.id}`} className="bg-black border border-white/10 px-2 py-1 text-xs">
                {INQUIRY_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {openId === i.id && (
              <div className="px-4 pb-4 border-t border-white/10 pt-3 text-sm grid sm:grid-cols-2 gap-x-6 gap-y-1">
                {[["Phone", i.phone], ["School/Club", i.school_club], ["Location", i.location], ["Participants", i.participants], ["Season", `${i.season_start || ""} - ${i.season_end || ""}`], ["Has branding", i.has_branding], ["Interested products", i.interested_products], ["Current provider", i.current_provider], ["Fundraising pref", i.fundraising_pref]].map(([l, v]) => (v && v !== " - ") ? <p key={l} className="text-gray-400"><span className="text-gray-500">{l}:</span> <span className="text-gray-200">{v}</span></p> : null)}
                {i.notes && <p className="sm:col-span-2 text-gray-300 mt-1"><span className="text-gray-500">Notes:</span> {i.notes}</p>}
                <div className="sm:col-span-2 flex gap-3 mt-1">
                  {i.logo_url && <a href={`${BACKEND_URL}${i.logo_url}`} target="_blank" rel="noreferrer" className="text-[var(--gv-blue-bright)] text-xs">View logo</a>}
                  {i.artwork_url && <a href={`${BACKEND_URL}${i.artwork_url}`} target="_blank" rel="noreferrer" className="text-[var(--gv-blue-bright)] text-xs">View artwork</a>}
                </div>
                <div className="sm:col-span-2 mt-2">
                  <label className="text-xs text-gray-500 uppercase tracking-wider">Admin notes</label>
                  <textarea defaultValue={i.admin_notes} onBlur={(e) => saveNotes(i.id, e.target.value)} data-testid={`inquiry-notes-${i.id}`} rows={2} className="w-full mt-1 bg-black border border-white/10 px-3 py-2 text-sm" />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const Metric = ({ label, value, sub, accent }) => (
  <div className={`border p-6 ${accent ? "border-[var(--gv-blue)] bg-[var(--gv-blue)]/10" : "border-white/10 bg-[#141518]"}`}>
    <p className="text-xs uppercase tracking-[0.2em] text-gray-400">{label}</p>
    <p className="font-display text-4xl mt-2 text-[var(--gv-blue-bright)]">{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

function Inventory() {
  const [inv, setInv] = useState([]);
  const load = () => api.get("/admin/inventory", { params: { store_slug: STORE_SLUG } }).then((r) => setInv(r.data));
  useEffect(() => { load(); }, []);

  const save = async (size, quantity) => {
    await api.patch("/admin/inventory", { size, quantity: parseInt(quantity) }, { params: { store_slug: STORE_SLUG } });
    toast.success(`${size} updated`);
    load();
  };

  return (
    <div>
      <p className="text-sm text-gray-400 mb-4">Blank garment stock — Team 365 TT11 (Black). Auto-deducts on each order.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="inventory-grid">
        {inv.map((i) => {
          const low = i.quantity <= (i.low_threshold ?? 3);
          return (
            <div key={i.size} className={`border p-4 ${low ? "border-yellow-500/50 bg-yellow-500/5" : "border-white/10 bg-[#141518]"}`}>
              <div className="flex items-center justify-between">
                <span className="font-display text-3xl">{i.size}</span>
                {low && <AlertTriangle className="w-4 h-4 text-yellow-500" data-testid={`low-stock-${i.size}`} />}
              </div>
              <input type="number" defaultValue={i.quantity} onBlur={(e) => save(i.size, e.target.value)} data-testid={`inv-input-${i.size}`}
                className="w-full mt-3 bg-black border border-white/10 px-3 py-2 text-sm" />
              <p className={`text-xs mt-1 ${low ? "text-yellow-500" : "text-gray-500"}`}>{low ? "Low stock" : "In stock"}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GameDayQR() {
  const target = `${window.location.origin}/gameday`;
  const qrSrc = `${API}/stores/${STORE_SLUG}/qr?target=${encodeURIComponent(target)}`;
  return (
    <div className="max-w-md">
      <p className="text-sm text-gray-400 mb-4">Print this QR code for volleyball games. It links fans directly to the Game Day landing page.</p>
      <div className="border border-white/10 bg-white p-6 inline-block">
        <img src={qrSrc} alt="Game Day QR" className="w-56 h-56" data-testid="gameday-qr" />
      </div>
      <p className="mt-4 text-xs text-gray-500 break-all">{target}</p>
    </div>
  );
}

const PM_CATS = [
  { slug: "player-gear", label: "Player Gear" },
  { slug: "parents-family", label: "Parents & Family" },
  { slug: "fan-gear", label: "Fan Gear" },
  { slug: "varsity", label: "Varsity" },
  { slug: "jv", label: "JV" },
  { slug: "middle-school", label: "Middle School" },
];

function ScheduleManager() {
  const [games, setGames] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/stores/${STORE_SLUG}`).then((r) => setGames(r.data.schedule || []));
  }, []);
  const upd = (i, k, v) => setGames((g) => g.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const addRow = () => setGames((g) => [...g, { opponent: "", datetime: "", location: "HOME", result: "", score: "" }]);
  const removeRow = (i) => setGames((g) => g.filter((_, idx) => idx !== i));
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/stores/${STORE_SLUG}/schedule`, { schedule: games });
      toast.success("Schedule saved");
    } catch { toast.error("Could not save"); }
    setBusy(false);
  };
  const inputCls = "bg-black border border-white/10 focus:border-[var(--gv-blue)] outline-none px-2 py-2 text-sm";
  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400">Season Schedule ({games.length} games)</p>
        <button onClick={addRow} data-testid="sch-add" className="flex items-center gap-1 text-xs text-[var(--gv-blue-bright)] font-bold"><Plus className="w-4 h-4" /> Add game</button>
      </div>
      <div className="space-y-2">
        {games.map((g, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center border border-white/10 bg-[#141518] p-2" data-testid={`sch-row-${i}`}>
            <input value={g.opponent} onChange={(e) => upd(i, "opponent", e.target.value)} placeholder="Opponent" data-testid={`sch-opp-${i}`} className={`${inputCls} col-span-4`} />
            <input type="datetime-local" value={(g.datetime || "").slice(0, 16)} onChange={(e) => upd(i, "datetime", e.target.value)} data-testid={`sch-dt-${i}`} className={`${inputCls} col-span-3`} />
            <select value={g.location} onChange={(e) => upd(i, "location", e.target.value)} data-testid={`sch-loc-${i}`} className={`${inputCls} col-span-1`}>
              <option value="HOME">Home</option>
              <option value="AWAY">Away</option>
            </select>
            <input value={g.result} onChange={(e) => upd(i, "result", e.target.value)} placeholder="W/L" data-testid={`sch-res-${i}`} className={`${inputCls} col-span-1`} />
            <input value={g.score} onChange={(e) => upd(i, "score", e.target.value)} placeholder="Score" data-testid={`sch-score-${i}`} className={`${inputCls} col-span-2`} />
            <button onClick={() => removeRow(i)} data-testid={`sch-del-${i}`} className="col-span-1 text-gray-500 hover:text-red-400 flex justify-center"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={busy} data-testid="sch-save" className="mt-4 bg-[var(--gv-blue)] hover:bg-[var(--gv-blue-bright)] text-black font-bold uppercase tracking-widest text-sm px-8 py-3 transition-colors disabled:opacity-50">{busy ? "Saving…" : "Save Schedule"}</button>
    </div>
  );
}

function StoreSettings() {
  const [s, setS] = useState({ hype_banner: "", banner_enabled: false, order_deadline: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/stores/${STORE_SLUG}`).then((r) => {
      const d = r.data;
      setS({ hype_banner: d.hype_banner || "", banner_enabled: !!d.banner_enabled, order_deadline: (d.order_deadline || "").slice(0, 16) });
    });
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/stores/${STORE_SLUG}/settings`, s);
      toast.success("Storefront banner updated");
    } catch { toast.error("Could not save"); }
    setBusy(false);
  };
  const inputCls = "w-full bg-black border border-white/10 focus:border-[var(--gv-blue)] outline-none px-3 py-2 text-sm";
  return (
    <div className="max-w-xl border border-white/10 bg-[#141518] p-6 space-y-4">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400">Storefront Banner & Countdown</p>
      <p className="text-[11px] text-gray-500 -mt-2">Shows a bar at the top of the store. Use the hype line for game hype (e.g. "BEAT RIDGEVIEW — Fri 7PM") and set an order deadline to display a live countdown before the print run closes.</p>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={s.banner_enabled} onChange={(e) => setS({ ...s, banner_enabled: e.target.checked })} data-testid="settings-enabled" /> Show banner on storefront</label>
      <div>
        <label className="text-xs text-gray-400 block mb-1">Hype message</label>
        <input value={s.hype_banner} onChange={(e) => setS({ ...s, hype_banner: e.target.value })} placeholder="BEAT RIDGEVIEW — Fri 7PM @ Home" data-testid="settings-hype" className={inputCls} />
      </div>
      <div>
        <label className="text-xs text-gray-400 block mb-1">Order-by deadline (for countdown)</label>
        <input type="datetime-local" value={s.order_deadline} onChange={(e) => setS({ ...s, order_deadline: e.target.value })} data-testid="settings-deadline" className={inputCls} />
      </div>
      <button onClick={save} disabled={busy} data-testid="settings-save" className="w-full bg-[var(--gv-blue)] hover:bg-[var(--gv-blue-bright)] text-black font-bold uppercase tracking-widest text-sm py-3 transition-colors disabled:opacity-50">{busy ? "Saving…" : "Save Banner"}</button>
    </div>
  );
}

function SeniorNightOrders() {
  const PLAYER = "sasha-hughes";
  const [data, setData] = useState({ orders: [], aggregate: [], paid_total: 0, paid_count: 0, order_count: 0 });
  const [page, setPage] = useState(null);
  const [cfg, setCfg] = useState({ order_deadline: "", pickup_note: "" });
  const load = () => {
    api.get(`/admin/players/${PLAYER}/orders`).then((r) => setData(r.data));
    api.get(`/stores/${STORE_SLUG}/players/${PLAYER}`).then((r) => {
      setPage(r.data);
      setCfg({ order_deadline: r.data.order_deadline || "", pickup_note: r.data.pickup_note || "" });
    });
  };
  useEffect(() => { load(); }, []);

  const exportCsv = () => {
    const token = localStorage.getItem("gv_admin_token");
    window.open(`${API}/admin/players/${PLAYER}/orders/export?token=${token}`, "_blank");
  };
  const saveCfg = async () => { await api.patch(`/admin/players/${PLAYER}`, cfg); toast.success("Page details updated"); load(); };
  const savePrice = async (pid, val) => {
    const flat_price = Number(val);
    if (!flat_price || flat_price <= 0) return toast.error("Enter a valid price");
    await api.patch(`/admin/players/${PLAYER}/products/${pid}`, { flat_price });
    toast.success("Price updated"); load();
  };
  const printRun = () => {
    const rows = data.aggregate.map((a) => `<tr><td>${a.category_label}</td><td>${a.size}</td><td style="text-align:center;font-weight:bold">${a.quantity}</td></tr>`).join("");
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Sasha Hughes Senior Night — Print Run</title>
      <style>body{font-family:Arial;padding:24px}h1{font-size:20px;margin:0 0 4px}p{color:#555;font-size:12px}
      table{width:100%;border-collapse:collapse;margin-top:16px}td,th{border:1px solid #ccc;padding:8px;font-size:13px;text-align:left}th{background:#eee}</style>
      </head><body><h1>Sasha Hughes — Senior Night 2026 Print Run</h1>
      <p>Paid orders only • ${data.paid_count} paid order(s) • generated ${new Date().toLocaleString()}</p>
      <table><thead><tr><th>Garment Category</th><th>Size</th><th>Qty</th></tr></thead><tbody>${rows || "<tr><td colspan=3>No paid orders yet</td></tr>"}</tbody></table>
      </body></html>`);
    w.document.close(); w.print();
  };
  const inputCls = "w-full bg-black border border-white/10 focus:border-[var(--gv-blue)] outline-none px-3 py-2 text-sm";

  return (
    <div className="space-y-8" data-testid="senior-night-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-gray-400">Sasha Hughes • Senior Night 2026 — {data.order_count} order(s), {data.paid_count} paid</p>
          <p className="font-display text-3xl">${Number(data.paid_total).toFixed(2)} <span className="text-sm text-gray-500 font-sans">paid revenue</span></p>
        </div>
        <div className="flex gap-2">
          <button onClick={printRun} data-testid="sn-print-run-btn" className="flex items-center gap-2 border border-white/20 px-4 py-2 text-sm font-bold uppercase tracking-wider"><Printer className="w-4 h-4" /> Print Run Sheet</button>
          <button onClick={exportCsv} data-testid="sn-export-btn" className="flex items-center gap-2 bg-[var(--gv-blue)] hover:bg-[var(--gv-blue-bright)] text-black px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors"><Download className="w-4 h-4" /> Export CSV</button>
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400 mb-3">Quantities by Category & Size (paid orders)</p>
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-[#141518] text-gray-400 text-xs uppercase tracking-wider">
              <tr>{["Garment Category", "Size", "Qty"].map((h) => <th key={h} className="text-left px-3 py-3">{h}</th>)}</tr>
            </thead>
            <tbody data-testid="sn-aggregate-table">
              {data.aggregate.length === 0 && <tr><td colSpan={3} className="px-3 py-6 text-center text-gray-500">No paid Senior Night orders yet</td></tr>}
              {data.aggregate.map((a, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="px-3 py-2">{a.category_label}</td>
                  <td className="px-3 py-2">{a.size}</td>
                  <td className="px-3 py-2 font-bold">{a.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400 mb-3">Orders</p>
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-[#141518] text-gray-400 text-xs uppercase tracking-wider">
              <tr>{["Order #", "Date", "Buyer", "Contact", "Items", "Subtotal", "Payment", "Fulfillment", "Status"].map((h) => <th key={h} className="text-left px-3 py-3 whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody data-testid="sn-orders-table">
              {data.orders.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-500">No Senior Night orders yet</td></tr>}
              {data.orders.map((o) => (
                <tr key={o.id} className="border-t border-white/5 align-top" data-testid={`sn-order-${o.order_number}`}>
                  <td className="px-3 py-3 font-bold whitespace-nowrap">{o.order_number}</td>
                  <td className="px-3 py-3 text-gray-400 whitespace-nowrap">{o.created_at?.slice(0, 10)}</td>
                  <td className="px-3 py-3 whitespace-nowrap">{o.customer_name}</td>
                  <td className="px-3 py-3 text-xs text-gray-400 whitespace-nowrap">{o.email}<br />{o.phone}</td>
                  <td className="px-3 py-3">
                    {o.items.map((it, i) => (
                      <div key={i} className="text-xs mb-1 last:mb-0">
                        <span className="font-bold text-white">{it.category_label}</span> • Size {it.size} × {it.quantity} <span className="text-gray-500">(${it.line_total?.toFixed(2)})</span>
                      </div>
                    ))}
                  </td>
                  <td className="px-3 py-3 font-bold whitespace-nowrap">${Number(o.player_subtotal).toFixed(2)}</td>
                  <td className={`px-3 py-3 text-xs font-bold uppercase ${o.payment_status === "paid" ? "text-green-400" : "text-yellow-500"}`}>{o.payment_status}</td>
                  <td className="px-3 py-3 text-xs text-gray-400">{o.fulfillment === "shipping" ? `Ship: ${o.shipping_address}` : (o.pickup_location || "Team Pickup")}</td>
                  <td className="px-3 py-3 text-xs">{o.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-white/10 bg-[#141518] p-5 space-y-3">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400">Page Details (shown on Sasha's page)</p>
          <textarea value={cfg.order_deadline} onChange={(e) => setCfg({ ...cfg, order_deadline: e.target.value })} data-testid="sn-deadline-input" rows={2} className={inputCls} placeholder="Ordering deadline" />
          <textarea value={cfg.pickup_note} onChange={(e) => setCfg({ ...cfg, pickup_note: e.target.value })} data-testid="sn-pickup-input" rows={3} className={inputCls} placeholder="Pickup instructions" />
          <button onClick={saveCfg} data-testid="sn-save-details" className="bg-[var(--gv-blue)] hover:bg-[var(--gv-blue-bright)] text-black font-bold uppercase tracking-widest text-xs px-4 py-2 transition-colors">Save Details</button>
        </div>
        <div className="border border-white/10 bg-[#141518] p-5 space-y-3">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400">Prices</p>
          {(page?.products || []).map((p) => (
            <div key={p.id} className="flex items-center gap-2">
              <span className="text-sm flex-1">{p.category_label}</span>
              <input defaultValue={p.flat_price} onBlur={(e) => savePrice(p.id, e.target.value)} data-testid={`sn-price-input-${p.size_category}`} className="w-24 bg-black border border-white/10 px-2 py-1 text-sm" />
            </div>
          ))}
          <p className="text-[11px] text-gray-500">Click out of a field to save. Prices apply to new orders only.</p>
        </div>
      </div>
    </div>
  );
}

function ProductManager() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const initialRows = [
    { name: "Black", hex: "#0A0A0A", front: null, back: null },
    { name: "Grey", hex: "#6B7280", front: null, back: null },
    { name: "White", hex: "#FFFFFF", front: null, back: null },
  ];
  const [colorRows, setColorRows] = useState(initialRows);
  const [form, setForm] = useState({ name: "", description: "", allow_personalization: true, popular: true });
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/stores/${STORE_SLUG}/products`).then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const toggleCat = (slug) => setCats((c) => c.includes(slug) ? c.filter((x) => x !== slug) : [...c, slug]);
  const updateRow = (i, key, val) => setColorRows((rows) => rows.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const addRow = () => setColorRows((rows) => [...rows, { name: "", hex: "#6B7280", front: null, back: null }]);
  const removeRow = (i) => setColorRows((rows) => rows.filter((_, idx) => idx !== i));
  const uploadFile = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return `${BACKEND_URL}${data.url}`;
  };
  const submit = async () => {
    const rows = colorRows.filter((r) => r.front);
    if (!form.name || rows.length === 0) return toast.error("Enter a product name and at least one color with a front photo");
    setBusy(true);
    try {
      const colors = [];
      for (const r of rows) {
        const frontUrl = await uploadFile(r.front);
        const backUrl = r.back ? await uploadFile(r.back) : "";
        colors.push({ name: r.name || "Default", hex: r.hex, front_image: frontUrl, back_image: backUrl });
      }
      await api.post(`/admin/stores/${STORE_SLUG}/products`, {
        name: form.name, description: form.description, categories: cats,
        allow_personalization: form.allow_personalization, popular: form.popular,
        colors, front_image: colors[0].front_image, back_image: colors[0].back_image, active: true,
      });
      toast.success("Product published to the store");
      setForm({ name: "", description: "", allow_personalization: true, popular: true });
      setColorRows(initialRows.map((r) => ({ ...r }))); setCats([]);
      load();
    } catch (e) { toast.error("Upload failed — try again"); }
    setBusy(false);
  };

  const fileCls = "w-full text-xs text-gray-400 file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-[var(--gv-blue)] file:text-black file:font-bold file:text-xs";
  const inputCls = "w-full bg-black border border-white/10 focus:border-[var(--gv-blue)] outline-none px-3 py-2 text-sm";

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="border border-white/10 bg-[#141518] p-6 space-y-3">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400">Upload New Design / Product</p>
        <p className="text-[11px] text-gray-500 -mt-2">One product = one front design. Add a front photo for each garment color you carry (Black / Grey / White). Back/roster design is optional per color.</p>
        <input placeholder="Product name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pm-name" className={inputCls} />
        <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="pm-desc" className={inputCls} rows={3} />

        <div className="space-y-3">
          <p className="text-xs text-gray-400 font-bold">Garment Colors & Front Photos</p>
          {colorRows.map((r, i) => (
            <div key={i} className="border border-white/10 p-3 space-y-2" data-testid={`pm-color-row-${i}`}>
              <div className="flex items-center gap-2">
                <input type="color" value={r.hex} onChange={(e) => updateRow(i, "hex", e.target.value)} data-testid={`pm-hex-${i}`} className="w-9 h-9 bg-black border border-white/10 flex-shrink-0" />
                <input placeholder="Color name" value={r.name} onChange={(e) => updateRow(i, "name", e.target.value)} data-testid={`pm-color-${i}`} className={inputCls} />
                {colorRows.length > 1 && <button onClick={() => removeRow(i)} data-testid={`pm-remove-${i}`} className="text-gray-500 hover:text-red-400 text-sm px-2 flex-shrink-0">✕</button>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-gray-500 block mb-1">Front photo *</label>
                  <input type="file" accept="image/*" onChange={(e) => updateRow(i, "front", e.target.files[0])} data-testid={`pm-front-${i}`} className={fileCls} />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 block mb-1">Back / roster (optional)</label>
                  <input type="file" accept="image/*" onChange={(e) => updateRow(i, "back", e.target.files[0])} data-testid={`pm-back-${i}`} className={fileCls} />
                </div>
              </div>
            </div>
          ))}
          <button onClick={addRow} data-testid="pm-add-color" className="text-xs text-[var(--gv-blue-bright)] font-bold">+ Add another color</button>
        </div>

        <div>
          <p className="text-xs text-gray-400 mb-1">Categories</p>
          <div className="flex flex-wrap gap-2">
            {PM_CATS.map((c) => (
              <button key={c.slug} onClick={() => toggleCat(c.slug)} data-testid={`pm-cat-${c.slug}`}
                className={`px-3 py-1 text-xs border transition-colors ${cats.includes(c.slug) ? "border-[var(--gv-blue)] bg-[var(--gv-blue)]/10 text-white" : "border-white/10 text-gray-400"}`}>{c.label}</button>
            ))}
          </div>
        </div>
        <div className="flex gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.allow_personalization} onChange={(e) => setForm({ ...form, allow_personalization: e.target.checked })} data-testid="pm-pers" /> Personalization</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.popular} onChange={(e) => setForm({ ...form, popular: e.target.checked })} data-testid="pm-popular" /> Popular</label>
        </div>
        <button onClick={submit} disabled={busy} data-testid="pm-submit"
          className="w-full flex items-center justify-center gap-2 bg-[var(--gv-blue)] hover:bg-[var(--gv-blue-bright)] text-black font-bold uppercase tracking-widest text-sm py-3 transition-colors disabled:opacity-50">
          <UploadCloud className="w-4 h-4" /> {busy ? "Publishing…" : "Publish Product"}
        </button>
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400 mb-3">Live Products ({products.length})</p>
        <div className="grid grid-cols-2 gap-3" data-testid="pm-product-list">
          {products.map((p) => (
            <div key={p.id} className="border border-white/10 bg-[#141518]">
              <img src={p.front_image} alt={p.name} className="w-full aspect-square object-cover bg-black" />
              <div className="p-2">
                <p className="text-xs font-bold">{p.name}</p>
                <p className="text-[10px] text-gray-500">{(p.colors || []).map((c) => c.name).join(" • ") || "1 color"}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
